import type { HelpCenterAction } from "@/lib/support/help-center-data";

export type HelpCenterFallbackAction = HelpCenterAction | "open_chat" | "open_inbox" | "open_order_help";

type WebSupportContext = {
  action?: string;
  categorySlug?: string;
  articleSlug?: string;
  orderId?: string;
  topic?: string;
  caseReference?: string;
  caseId?: string;
};

const ACTION_LABELS: Record<string, string> = {
  OPEN_NATIVE_CHAT: "Chat with support",
  OPEN_NATIVE_SUPPORT_INBOX: "Open support inbox",
  OPEN_NATIVE_ORDER_HELP: "Get help with this order",
  open_chat: "Chat with support",
  open_inbox: "Open support inbox",
  open_order_help: "Get help with this order",
};

function normalizeValue(value?: string): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function getSupportActionLabel(action?: string): string {
  const normalizedAction = normalizeValue(action);
  if (!normalizedAction) {
    return "Support request";
  }
  return ACTION_LABELS[normalizedAction] || "Support request";
}

export function buildWebSupportContactHref(context: WebSupportContext = {}): string {
  const params = new URLSearchParams();
  const action = normalizeValue(context.action);
  const categorySlug = normalizeValue(context.categorySlug);
  const articleSlug = normalizeValue(context.articleSlug);
  const orderId = normalizeValue(context.orderId);
  const topic = normalizeValue(context.topic);
  const caseReference = normalizeValue(context.caseReference) || normalizeValue(context.caseId);

  if (action) params.set("action", action);
  if (categorySlug) params.set("categorySlug", categorySlug);
  if (articleSlug) params.set("articleSlug", articleSlug);
  if (orderId) params.set("orderId", orderId);
  if (topic) params.set("topic", topic);
  if (caseReference) params.set("caseReference", caseReference);

  const query = params.toString();
  return query.length > 0 ? `/support/contact?${query}` : "/support/contact";
}

export function buildWebSupportFormHref(context: WebSupportContext = {}): string {
  const params = new URLSearchParams();
  params.set("subject", "support");
  params.set("source", "help_center");
  const caseId = normalizeValue(context.caseId);
  if (caseId) params.set("caseId", caseId);

  const action = normalizeValue(context.action);
  const categorySlug = normalizeValue(context.categorySlug);
  const articleSlug = normalizeValue(context.articleSlug);
  const orderId = normalizeValue(context.orderId);
  const topic = normalizeValue(context.topic);
  const caseReference = normalizeValue(context.caseReference) || normalizeValue(context.caseId);

  if (action) params.set("action", action);
  if (categorySlug) params.set("categorySlug", categorySlug);
  if (articleSlug) params.set("articleSlug", articleSlug);
  if (orderId) params.set("orderId", orderId);
  if (topic) params.set("topic", topic);
  if (caseReference) params.set("caseReference", caseReference);

  return `/contact?${params.toString()}`;
}

export function buildSupportPrefillMessage(context: WebSupportContext = {}): string {
  const details: string[] = [];
  const actionLabel = getSupportActionLabel(context.action);
  const categorySlug = normalizeValue(context.categorySlug);
  const articleSlug = normalizeValue(context.articleSlug);
  const orderId = normalizeValue(context.orderId);
  const topic = normalizeValue(context.topic);
  const caseReference = normalizeValue(context.caseReference) || normalizeValue(context.caseId);

  details.push("I need help from the Help Center.");
  details.push("");
  details.push(`Requested action: ${actionLabel}`);

  if (categorySlug || articleSlug) {
    details.push(
      `Article context: ${categorySlug || "unknown"}${articleSlug ? ` / ${articleSlug}` : ""}`
    );
  }

  if (orderId) {
    details.push(`Order context: ${orderId}`);
  }

  if (topic) {
    details.push(`Topic: ${topic}`);
  }

  if (caseReference) {
    details.push(`Case reference: ${caseReference}`);
  }

  details.push("");
  details.push("Issue details:");
  return details.join("\n");
}
